<template>
  <v-card flat class="border-0 !bg-[rgb(var(--v-theme-surface))]">
    <!-- ═══ POST-GENERATION: Settlement Complete ═══ -->
    <template v-if="statementReady">
      <v-card-title class="text-h6 font-weight-bold pb-0"
        >Settlement Complete</v-card-title
      >
      <v-card-text>
        <div
          class="flex flex-col items-center gap-4 py-8"
          data-cy="statement-ready-alert"
        >
          <!-- Generated badge -->
          <span
            class="inline-flex items-center gap-1 px-4 py-1 rounded-full text-green-700 bg-green-100 text-sm font-medium"
          >
            ✓ Settlement Statement Generated
          </span>

          <div class="text-center">
            <div class="text-body-1 font-weight-bold mb-1">
              Statement generated on {{ generatedDateFormatted }}
            </div>
            <div class="text-body-2 text-grey-darken-1">
              This is the final settlement statement on file. You can reprint it
              or unlock to make changes and generate a new version.
            </div>
          </div>

          <!-- Action buttons -->
          <div
            class="flex flex-wrap justify-center gap-3 mt-2"
            data-cy="post-generation-actions"
          >
            <LlSecondaryButton
              color="primary"
              prepend-icon="mdi-printer"
              data-cy="reprint-button"
              @click="$emit('reprint')"
            >
              Reprint Statement
            </LlSecondaryButton>
            <a
              v-if="downloadUrl"
              :href="downloadUrl"
              download="settlement-statement.pdf"
              class="text-decoration-none"
              data-cy="download-pdf-link"
              @click="$emit('download')"
            >
              <LlSecondaryButton prepend-icon="mdi-download">
                Download PDF
              </LlSecondaryButton>
            </a>
            <LlTooltip v-if="!canReset" location="top">
              <div class="text-center whitespace-pre-line">
                {{ resetBlockedReason || 'Reset is not available' }}
              </div>
              <template #activator>
                <LlSecondaryButton
                  color="orange"
                  prepend-icon="mdi-lock"
                  data-cy="unlock-reset-button"
                  disabled
                >
                  Unlock &amp; Reset
                </LlSecondaryButton>
              </template>
            </LlTooltip>
            <LlSecondaryButton
              v-else
              color="orange"
              prepend-icon="mdi-lock-open"
              data-cy="unlock-reset-button"
              @click="$emit('unlock-and-reset')"
            >
              Unlock &amp; Reset
            </LlSecondaryButton>
          </div>
        </div>
      </v-card-text>
    </template>

    <!-- ═══ POLLING / GENERATING: Waiting for PDF ═══ -->
    <template v-else-if="generating || polling">
      <v-card-title class="text-h6 font-weight-bold pb-0"
        >Step {{ stepNumber }}: Generate Settlement Statement</v-card-title
      >
      <v-card-text>
        <div
          class="flex flex-col items-center py-8 gap-4"
          data-cy="polling-state"
        >
          <v-progress-circular indeterminate color="success" size="48" />
          <div class="text-body-1 font-weight-medium">
            Generating settlement statement…
          </div>
          <div class="text-caption text-grey">
            This usually takes a few seconds. Please wait.
          </div>
        </div>
      </v-card-text>
    </template>

    <!-- ═══ TIMED OUT ═══ -->
    <template v-else-if="timedOut">
      <v-card-title class="text-h6 font-weight-bold pb-0"
        >Step {{ stepNumber }}: Generate Settlement Statement</v-card-title
      >
      <v-card-text>
        <AlertBar
          class="mb-4"
          data-cy="timeout-alert"
        >
          <div class="font-weight-bold mb-1">Still generating…</div>
          <p class="mb-2 text-body-2">
            The statement is taking longer than expected. The PDF may still be
            processing.
          </p>
          <LlSecondaryButton
            color="warning"
            :loading="saving"
            data-cy="retry-polling-button"
            @click="$emit('retry-polling')"
          >
            Check again
          </LlSecondaryButton>
        </AlertBar>
      </v-card-text>
    </template>

    <!-- ═══ PRE-GENERATION: Ready to generate ═══ -->
    <template v-else>
      <v-card-title class="text-h6 font-weight-bold pb-0"
        >Step {{ stepNumber }}: Generate Settlement Statement</v-card-title
      >
      <v-card-text>
        <!-- Cannot-generate alert -->
        <AlertBar
          v-if="!canGenerate"
          class="mb-4"
          data-cy="cannot-generate-alert"
        >
          All prior steps (1–5) must be completed before you can generate the settlement statement.
        </AlertBar>

        <!-- Info about what happens when you generate -->
        <AlertBar type="info" class="mb-4" data-cy="generation-info-alert">
          Generating the statement will lock the settlement and produce a PDF. You can unlock and reset afterwards if changes are needed.
        </AlertBar>

        <!-- Preview PDF loading state -->
        <div
          v-if="previewGenerating || previewPolling"
          class="flex flex-col items-center py-8 gap-4"
          data-cy="preview-loading-state"
        >
          <v-progress-circular indeterminate color="blue" size="48" />
          <div class="text-body-1 font-weight-medium">Generating statement preview…</div>
          <div class="text-caption text-grey">
            This usually takes a few seconds.
          </div>
        </div>

        <!-- Preview PDF timed out -->
        <AlertBar
          v-else-if="previewTimedOut"
          class="mb-4"
          data-cy="preview-timeout-alert"
        >
          <div class="font-weight-bold mb-1">Preview taking longer than expected…</div>
          <p class="mb-2 text-body-2">
            The preview PDF is still being generated.
          </p>
          <LlSecondaryButton
            color="warning"
            data-cy="retry-preview-button"
            @click="$emit('retry-preview')"
          >
            Try again
          </LlSecondaryButton>
        </AlertBar>

        <!-- Embedded preview PDF viewer -->
        <div
          v-else-if="previewUrl"
          class="w-full border rounded mb-4 overflow-auto"
          style="max-height: 600px"
          data-cy="statement-preview-pre"
        >
          <VuePdfEmbed :source="previewUrl" />
        </div>

        <div class="flex gap-3 mt-2">
          <LlSecondaryButton @click="$emit('go-back')" data-cy="back-button">
            &larr; Back
          </LlSecondaryButton>
          <LlSecondaryButton
            v-if="canGenerate"
            color="primary"
            :loading="saving"
            data-cy="generate-button"
            @click="$emit('generate')"
          >
            Generate Statement
          </LlSecondaryButton>
        </div>
      </v-card-text>
    </template>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import AlertBar from '@/components/AlertBar.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import { formatLongDateString } from '@/services/utils';
import { VuePdfEmbed } from '@/utils/pdfHelpers';

const props = defineProps({
  /** Number shown in the title — the parent renumbers when Map Payments is skipped. */
  stepNumber: { type: Number, default: 6 },
  /** Whether statement PDF has been generated and downloadUrl is set. */
  statementReady: { type: Boolean, default: false },
  /** Whether we are currently polling for the blobPath. */
  polling: { type: Boolean, default: false },
  /** Whether polling timed out without finding a blobPath. */
  timedOut: { type: Boolean, default: false },
  /** Whether the POST /statement/generate request is in-flight. */
  generating: { type: Boolean, default: false },
  /** Whether any save is in progress (used for retry button). */
  saving: { type: Boolean, default: false },
  /** Authenticated download URL for the PDF (non-null when statementReady). */
  downloadUrl: { type: String, default: null },
  /** ISO date string when the statement was generated (for post-gen display). */
  generatedDate: { type: String, default: null },
  /** Whether all prior steps are complete and generate is allowed. */
  canGenerate: { type: Boolean, default: true },
  /** Authenticated blob URL for the preview PDF (pre-generation inline display). */
  previewUrl: { type: String, default: null },
  /** Whether the preview PDF is being generated (POST in-flight). */
  previewGenerating: { type: Boolean, default: false },
  /** Whether we are polling for the preview blobPath. */
  previewPolling: { type: Boolean, default: false },
  /** Whether preview polling timed out. */
  previewTimedOut: { type: Boolean, default: false },
  /** Whether the settlement can be reset (from backend). */
  canReset: { type: Boolean, default: true },
  /** Reason the reset is blocked (shown as tooltip when canReset is false). */
  resetBlockedReason: { type: String, default: null },
});

defineEmits([
  'retry-polling',
  'unlock-and-reset',
  'reprint',
  'download',
  'go-back',
  'generate', 'retry-preview',
]);

const generatedDateFormatted = computed(() => {
  if (!props.generatedDate) return '';
  return formatLongDateString(props.generatedDate) || props.generatedDate;
});

</script>
